import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, X, ArrowUpDown, ChevronDown, Star, Globe, Lock, FileText, LayoutDashboard, List, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

type SortOption = 'a-z' | 'z-a' | 'newest' | 'oldest';
type FilterOption = 'all' | 'bookmarked' | 'live' | 'private' | 'draft';

interface DashboardHeaderProps {
  viewMode: 'grid' | 'list';
  setViewMode: (mode: 'grid' | 'list') => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortBy: SortOption;
  setSortBy: (sort: SortOption) => void;
  filterBy: FilterOption;
  setFilterBy: (filter: FilterOption) => void;
  showSortMenu: boolean;
  setShowSortMenu: (show: boolean) => void;
  showFilterMenu: boolean;
  setShowFilterMenu: (show: boolean) => void;
  handleSortChange: (option: SortOption) => void;
  handleFilterChange: (option: FilterOption) => void;
  clearSearch: () => void;
  isMobile?: boolean;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  viewMode,
  setViewMode,
  searchQuery,
  setSearchQuery,
  sortBy,
  filterBy,
  showSortMenu,
  setShowSortMenu,
  showFilterMenu,
  setShowFilterMenu,
  handleSortChange,
  handleFilterChange,
  clearSearch,
  isMobile = false
}) => {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) {
      inputRef.current?.focus();
    }
  }, [searchOpen]);

  const handleSearchClose = () => {
    setSearchOpen(false);
    clearSearch();
  };

  const sortOptions = [
    { value: 'a-z', label: 'A-Z' },
    { value: 'z-a', label: 'Z-A' },
    { value: 'newest', label: 'Newest to Oldest' },
    { value: 'oldest', label: 'Oldest to Newest' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Brands', icon: null },
    { value: 'bookmarked', label: 'Bookmarked', icon: Star },
    { value: 'live', label: 'Live', icon: Globe },
    { value: 'private', label: 'Private', icon: Lock },
    { value: 'draft', label: 'Draft', icon: FileText }
  ];

  const isSortActive = sortBy !== 'a-z';

  return (
    <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
      <div className="flex items-center space-x-4">
        <h1 className="text-3xl font-bold text-foreground">Brand Kits</h1>
      </div>

      <div className={`flex flex-wrap items-center gap-2 ${isMobile ? 'hidden' : ''}`}>

        {/* Search — icon-only by default, expands on click */}
        <div className="relative flex items-center h-10">
          <AnimatePresence initial={false}>
            {searchOpen ? (
              <motion.div
                key="search-open"
                className="relative flex items-center h-10"
                initial={{ width: 40, opacity: 0.6 }}
                animate={{ width: 256, opacity: 1 }}
                exit={{ width: 40, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="w-4 h-4 text-muted-foreground" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search brands..."
                  onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                  className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
                <button
                  onMouseDown={(e) => { e.preventDefault(); handleSearchClose(); }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            ) : (
              <motion.button
                key="search-icon"
                onClick={() => setSearchOpen(true)}
                className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Search className="w-4 h-4" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* View Toggle — animated sliding pill */}
        <div className="relative flex items-center rounded-lg border border-border p-1 bg-card h-10">
          <motion.div
            className="absolute top-1 bottom-1 rounded-sm bg-background shadow-sm"
            initial={false}
            animate={{
              left: viewMode === 'grid' ? '4px' : 'calc(50%)',
              width: 'calc(50% - 4px)',
            }}
            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          />
          <button
            onClick={() => setViewMode('grid')}
            className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'grid' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <LayoutDashboard className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'list' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>

        {/* Filter Button */}
        <div className="relative h-10">
          <button
            onClick={() => setShowFilterMenu(!showFilterMenu)}
            className={`flex items-center space-x-2 px-4 h-full rounded-lg border transition-colors ${filterBy !== 'all'
                ? 'bg-background shadow-sm text-foreground border-transparent'
                : 'bg-card border-border text-foreground hover:bg-muted'
              }`}
            title="Filter brands"
          >
            {(() => {
              const filterOption = filterOptions.find(option => option.value === filterBy);
              const FilterIcon = filterOption?.icon;
              return (
                <>
                  {FilterIcon ? <FilterIcon className="w-4 h-4" /> : <ArrowUpDown className="w-4 h-4" />}
                  <span className="text-sm font-medium whitespace-nowrap">
                    {filterOption?.label || 'Filter'}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} />
                </>
              );
            })()}
          </button>

          <AnimatePresence>
            {showFilterMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowFilterMenu(false)} />
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                >
                  {filterOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleFilterChange(option.value as FilterOption)}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${filterBy === option.value
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-foreground hover:bg-accent'
                          }`}
                      >
                        <div className="flex items-center space-x-2">
                          {Icon && <Icon className="w-4 h-4" />}
                          <span>{option.label}</span>
                        </div>
                        {filterBy === option.value && (
                          <div className="w-2 h-2 bg-current rounded-full" />
                        )}
                      </button>
                    );
                  })}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Sort Button */}
        <div className="relative h-10">
          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            className={`p-2.5 h-full rounded-lg border transition-colors ${isSortActive
                ? 'bg-background shadow-sm text-foreground border-transparent'
                : 'bg-card border-border text-foreground hover:bg-muted'
              }`}
            title="Sort brands"
          >
            <Filter className="w-4 h-4" />
          </button>

          <AnimatePresence>
            {showSortMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowSortMenu(false)} />
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                >
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleSortChange(option.value as SortOption)}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${sortBy === option.value
                          ? 'bg-background shadow-sm text-foreground'
                          : 'text-foreground hover:bg-accent'
                        }`}
                    >
                      <span>{option.label}</span>
                      {sortBy === option.value && (
                        <div className="w-2 h-2 bg-current rounded-full" />
                      )}
                    </button>
                  ))}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* New Brand Button */}
        <button
          onClick={() => navigate('/onboarding')}
          className="flex items-center space-x-2 px-6 h-10 bg-workspace-accent text-white rounded-md transition-colors hover:bg-workspace-accent-hover whitespace-nowrap"
        >
          <Plus className="w-5 h-5" />
          <span>New Brand</span>
        </button>
      </div>
    </div>
  );
};
